import React, { useState, useEffect, useMemo, useCallback, Component, ErrorInfo, ReactNode } from 'react';
import { dashboardApi } from '../api/dashboard.api';
import FDTreemapChart, { FDTreemapBotData, TreemapNode } from '../components/dashboard/FDTreemapChart';
import BotDetailModal from '../components/dashboard/BotDetailModal';

interface FDTreemapResponse {
  success: boolean;
  date: string;
  currentHour: number;
  loadTime: number;
  totalBots: number;
  bots: {
    [botId: string]: FDTreemapBotData;
  };
}

/**
 * Get color based on variation percentage
 */
const getColorForVariation = (variation: number): string => {
  if (typeof variation !== 'number' || isNaN(variation)) return '#374151';
  if (variation >= 50) return '#15803d';
  if (variation >= 20) return '#22c55e';
  if (variation >= 5) return '#4ade80';
  if (variation > -5) return '#374151';
  if (variation > -20) return '#f87171';
  if (variation > -50) return '#ef4444';
  return '#b91c1c';
};

/**
 * Error Boundary Component
 */
interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('FDTreemap Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '20px',
          backgroundColor: '#7f1d1d',
          borderRadius: '8px',
          color: '#fca5a5',
          margin: '20px'
        }}>
          <h2>Erro ao renderizar o componente</h2>
          <p>{this.state.error?.message}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 16px',
              backgroundColor: '#ef4444',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '10px'
            }}
          >
            Recarregar pagina
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * FD Treemap Page Content
 */
const FDTreemapContent: React.FC = () => {
  const [data, setData] = useState<FDTreemapResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [minVolume, setMinVolume] = useState(0);
  const [loadTime, setLoadTime] = useState<number | null>(null);
  const [selectedBot, setSelectedBot] = useState<(FDTreemapBotData & { botId: string }) | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch data function
  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      console.log('Fetching FD Treemap data...');
      const startTime = Date.now();
      const response = await dashboardApi.getFDTreemapHourlyData();
      const elapsed = Date.now() - startTime;
      setLoadTime(elapsed);
      console.log('FD Treemap response:', response?.success, 'totalBots:', response?.totalBots);

      if (response?.success) {
        setData(response);
      } else {
        setError('Falha ao carregar dados');
      }
    } catch (err: any) {
      console.error('FD Treemap fetch error:', err);
      setError(err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle bot click
  const handleBotClick = useCallback((botId: string, botData: FDTreemapBotData) => {
    setSelectedBot({ ...botData, botId });
  }, []);

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setSelectedBot(null);
  }, []);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedBot) {
        setSelectedBot(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedBot]);

  // Get unique clients for filter dropdown
  const uniqueClients = useMemo(() => {
    if (!data?.bots) return [];
    const clients = new Set<string>();
    Object.values(data.bots).forEach(bot => {
      if (bot.client_name) clients.add(bot.client_name);
    });
    return Array.from(clients).sort();
  }, [data]);

  // Process and filter data for treemap
  const treemapData: TreemapNode[] = useMemo(() => {
    if (!data?.bots) return [];

    try {
      return Object.entries(data.bots)
        .map(([botId, botData]) => ({
          name: botData.bot_name || `Bot ${botId}`,
          size: botData.total_hoje || 0,
          value: botData.total_hoje || 0,
          variation: typeof botData.variation_vs_semana === 'number' ? botData.variation_vs_semana : 0,
          fill: getColorForVariation(botData.variation_vs_semana),
          botId,
          botData
        }))
        .filter(node => {
          // Must have positive size
          if (node.size <= 0) return false;
          // Apply search filter
          if (searchTerm && !node.name.toLowerCase().includes(searchTerm.toLowerCase())) {
            return false;
          }
          // Apply client filter
          if (clientFilter && node.botData.client_name !== clientFilter) {
            return false;
          }
          // Apply minimum volume filter
          if (node.size < minVolume) {
            return false;
          }
          return true;
        })
        .sort((a, b) => b.size - a.size);
    } catch (err) {
      console.error('Error processing treemap data:', err);
      return [];
    }
  }, [data, searchTerm, clientFilter, minVolume]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!treemapData.length) return null;

    const totalInteractions = treemapData.reduce((sum, node) => sum + node.size, 0);
    const avgVariation = treemapData.reduce((sum, node) => sum + node.variation, 0) / treemapData.length;
    const positiveCount = treemapData.filter(node => node.variation > 5).length;
    const negativeCount = treemapData.filter(node => node.variation < -5).length;
    const neutralCount = treemapData.length - positiveCount - negativeCount;

    return {
      totalBots: treemapData.length,
      totalInteractions,
      avgVariation,
      positiveCount,
      negativeCount,
      neutralCount
    };
  }, [treemapData]);

  // Detect anomalies - bots performing below expected
  const [showAllAnomalies, setShowAllAnomalies] = useState(false);
  const ANOMALY_LIMIT = 30;

  const allAnomalies = useMemo(() => {
    if (!data?.bots) return [];

    return Object.entries(data.bots)
      .map(([botId, botData]) => ({
        botId,
        ...botData
      }))
      .filter(bot => {
        // Only consider bots with expected values (had activity in past periods)
        const hasExpectedActivity = (bot.expected_ontem || 0) > 10 || (bot.expected_semana || 0) > 10;
        // Anomaly score > 30 means significant underperformance
        return hasExpectedActivity && (bot.anomaly_score || 0) > 30;
      })
      .sort((a, b) => (b.anomaly_score || 0) - (a.anomaly_score || 0));
  }, [data]);

  // Apply limit unless showing all
  const anomalies = showAllAnomalies ? allAnomalies : allAnomalies.slice(0, ANOMALY_LIMIT);

  const inputStyle: React.CSSProperties = {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid rgba(75, 85, 99, 0.4)',
    backgroundColor: 'rgba(31, 41, 55, 0.8)',
    color: '#fff',
    fontSize: '13px',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s'
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'rgba(31, 41, 55, 0.6)',
    borderRadius: '10px',
    padding: '14px 16px',
    border: '1px solid rgba(75, 85, 99, 0.3)'
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#0f172a', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
            <h1 style={{ color: '#fff', fontSize: '22px', fontWeight: '600', margin: 0 }}>
              Mapa de Calor
            </h1>
            <span style={{ color: '#6b7280', fontSize: '14px' }}>FDs / Bots</span>
          </div>
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid rgba(75, 85, 99, 0.4)',
              backgroundColor: refreshing ? 'rgba(75, 85, 99, 0.2)' : 'rgba(59, 130, 246, 0.2)',
              color: refreshing ? '#6b7280' : '#60a5fa',
              fontSize: '13px',
              fontWeight: '500',
              cursor: refreshing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
          >
            <span style={{
              display: 'inline-block',
              animation: refreshing ? 'spin 1s linear infinite' : 'none'
            }}>
              ↻
            </span>
            {refreshing ? 'Atualizando...' : 'Atualizar'}
          </button>
        </div>
        {loadTime !== null && (
          <p style={{ color: '#4b5563', fontSize: '12px', margin: 0 }}>
            {treemapData.length} de {data?.totalBots || 0} bots · {loadTime}ms · hora {data?.currentHour || 0}h
          </p>
        )}
      </div>

      {/* Filters + Stats Row */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '16px',
        flexWrap: 'wrap',
        alignItems: 'stretch'
      }}>
        {/* Filters */}
        <div style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          backgroundColor: 'rgba(31, 41, 55, 0.4)',
          borderRadius: '10px',
          padding: '10px 14px',
          border: '1px solid rgba(75, 85, 99, 0.2)'
        }}>
          <input
            type="text"
            placeholder="Buscar bot..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ ...inputStyle, width: '160px' }}
          />
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            style={{ ...inputStyle, width: '170px', cursor: 'pointer' }}
          >
            <option value="">Todos clientes</option>
            {uniqueClients.map(client => (
              <option key={client} value={client}>{client}</option>
            ))}
          </select>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: '#6b7280', fontSize: '12px' }}>Min:</span>
            <input
              type="number"
              value={minVolume}
              onChange={(e) => setMinVolume(parseInt(e.target.value) || 0)}
              style={{ ...inputStyle, width: '70px', textAlign: 'center' }}
              min={0}
            />
          </div>
          {(searchTerm || clientFilter || minVolume > 0) && (
            <button
              onClick={() => { setSearchTerm(''); setClientFilter(''); setMinVolume(0); }}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid rgba(75, 85, 99, 0.4)',
                backgroundColor: 'transparent',
                color: '#9ca3af',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              Limpar
            </button>
          )}
        </div>

        {/* Quick Stats */}
        {summaryStats && (
          <div style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            marginLeft: 'auto'
          }}>
            <div style={{ ...cardStyle, textAlign: 'center', minWidth: '80px' }}>
              <div style={{ color: '#60a5fa', fontSize: '18px', fontWeight: '700' }}>
                {summaryStats.totalInteractions.toLocaleString()}
              </div>
              <div style={{ color: '#6b7280', fontSize: '10px' }}>interacoes</div>
            </div>
            <div style={{ ...cardStyle, textAlign: 'center', minWidth: '55px' }}>
              <div style={{ color: '#4ade80', fontSize: '16px', fontWeight: '700' }}>
                {summaryStats.positiveCount}
              </div>
              <div style={{ color: '#6b7280', fontSize: '10px' }}>alta</div>
            </div>
            <div style={{ ...cardStyle, textAlign: 'center', minWidth: '55px' }}>
              <div style={{ color: '#fca5a5', fontSize: '16px', fontWeight: '700' }}>
                {summaryStats.negativeCount}
              </div>
              <div style={{ color: '#6b7280', fontSize: '10px' }}>queda</div>
            </div>
            <div style={{ ...cardStyle, textAlign: 'center', minWidth: '55px' }}>
              <div style={{ color: '#9ca3af', fontSize: '16px', fontWeight: '700' }}>
                {summaryStats.neutralCount}
              </div>
              <div style={{ color: '#6b7280', fontSize: '10px' }}>estavel</div>
            </div>
          </div>
        )}
      </div>

      {/* Legend Bar - Compact */}
      <div style={{
        display: 'flex',
        gap: '6px',
        marginBottom: '16px',
        alignItems: 'center',
        padding: '8px 12px',
        backgroundColor: 'rgba(31, 41, 55, 0.3)',
        borderRadius: '8px',
        fontSize: '10px',
        color: '#6b7280',
        flexWrap: 'wrap'
      }}>
        <span style={{ marginRight: '4px' }}>Variacao vs semana:</span>
        {[
          { color: '#15803d', label: '+50%' },
          { color: '#22c55e', label: '+20%' },
          { color: '#4ade80', label: '+5%' },
          { color: '#374151', label: '0%' },
          { color: '#f87171', label: '-5%' },
          { color: '#ef4444', label: '-20%' },
          { color: '#b91c1c', label: '-50%' }
        ].map(({ color, label }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <div style={{ width: '12px', height: '12px', backgroundColor: color, borderRadius: '3px' }} />
            <span>{label}</span>
          </div>
        ))}
        <span style={{ margin: '0 8px', color: '#374151' }}>|</span>
        <span>Grafico:</span>
        <span style={{ color: '#60a5fa' }}>━</span><span>hoje</span>
        <span style={{ color: '#a78bfa' }}>━</span><span>ontem</span>
        <span style={{ color: '#34d399' }}>┅</span><span>semana</span>
      </div>

      {/* Anomaly Detection Panel */}
      {anomalies.length > 0 && (
        <div style={{
          backgroundColor: 'rgba(127, 29, 29, 0.15)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px',
          border: '1px solid rgba(239, 68, 68, 0.3)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#ef4444',
                animation: 'pulse 2s infinite'
              }} />
              <span style={{ color: '#fca5a5', fontWeight: '600', fontSize: '14px' }}>
                {showAllAnomalies ? allAnomalies.length : Math.min(anomalies.length, ANOMALY_LIMIT)}
                {!showAllAnomalies && allAnomalies.length > ANOMALY_LIMIT && ` de ${allAnomalies.length}`} Bot{allAnomalies.length > 1 ? 's' : ''} com Anomalia
              </span>
              <span style={{ color: '#6b7280', fontSize: '12px' }}>
                · performando abaixo do esperado ate a hora {data?.currentHour || 0}h
              </span>
            </div>
            {allAnomalies.length > ANOMALY_LIMIT && (
              <button
                onClick={() => setShowAllAnomalies(!showAllAnomalies)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  color: '#fca5a5',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {showAllAnomalies ? `Mostrar ${ANOMALY_LIMIT}` : `Ver todos (${allAnomalies.length})`}
              </button>
            )}
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '10px'
          }}>
            {anomalies.map((bot) => {
              const isCritical = (bot.anomaly_score || 0) > 60;
              return (
                <div
                  key={bot.botId}
                  onClick={() => handleBotClick(bot.botId, bot)}
                  style={{
                    backgroundColor: isCritical ? 'rgba(239, 68, 68, 0.15)' : 'rgba(251, 191, 36, 0.1)',
                    borderRadius: '8px',
                    padding: '12px',
                    border: isCritical ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(251, 191, 36, 0.2)',
                    cursor: 'pointer',
                    transition: 'transform 0.15s, box-shadow 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div>
                      <div style={{ color: '#fff', fontWeight: '600', fontSize: '13px' }}>
                        {bot.bot_name}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '10px' }}>
                        {bot.client_name}
                      </div>
                    </div>
                    <div style={{
                      backgroundColor: isCritical ? 'rgba(239, 68, 68, 0.3)' : 'rgba(251, 191, 36, 0.3)',
                      color: isCritical ? '#fca5a5' : '#fcd34d',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '9px',
                      fontWeight: '700'
                    }}>
                      {isCritical ? 'CRITICO' : 'ALERTA'}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '16px', fontSize: '11px' }}>
                    <div>
                      <span style={{ color: '#6b7280' }}>Atual: </span>
                      <span style={{ color: '#60a5fa', fontWeight: '600' }}>{(bot.total_hoje || 0).toLocaleString()}</span>
                    </div>
                    <div>
                      <span style={{ color: '#6b7280' }}>Esperado: </span>
                      <span style={{ color: '#a78bfa', fontWeight: '600' }}>{(bot.expected_ontem || 0).toLocaleString()}</span>
                      <span style={{ color: '#6b7280' }}> / </span>
                      <span style={{ color: '#34d399', fontWeight: '600' }}>{(bot.expected_semana || 0).toLocaleString()}</span>
                    </div>
                    <div>
                      <span style={{ color: '#6b7280' }}>Deficit: </span>
                      <span style={{ color: '#fca5a5', fontWeight: '600' }}>
                        -{Math.max(bot.deficit_ontem || 0, bot.deficit_semana || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div style={{
          backgroundColor: 'rgba(127, 29, 29, 0.8)',
          borderRadius: '8px',
          padding: '14px 16px',
          marginBottom: '16px',
          color: '#fca5a5',
          border: '1px solid rgba(239, 68, 68, 0.3)'
        }}>
          {error}
        </div>
      )}

      {/* Treemap Container */}
      <div style={{
        backgroundColor: 'rgba(17, 24, 39, 0.6)',
        borderRadius: '12px',
        padding: '12px',
        border: '1px solid rgba(75, 85, 99, 0.2)'
      }}>
        <FDTreemapChart
          data={treemapData}
          loading={loading}
          onBotClick={handleBotClick}
        />
      </div>

      {/* Bot Detail Modal */}
      {selectedBot && (
        <BotDetailModal
          bot={selectedBot}
          currentHour={data?.currentHour || 0}
          onClose={handleCloseModal}
        />
      )}

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

/**
 * FD Treemap Page with Error Boundary
 */
const FDTreemap: React.FC = () => {
  return (
    <ErrorBoundary>
      <FDTreemapContent />
    </ErrorBoundary>
  );
};

export default FDTreemap;
